/* eslint-disable quotes */
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import Modal from "../../Components/Modals/Modal";
import DeepLinkErrorModal from "../../Components/Modals/DeepLinkErrorModal/DeepLinkErrorModal";
import TestCodeButton from "../../Components/TestCodeButton/TestCodeButton";

import { selectControls } from "../../redux/controls";

import HomePageImage from "../../images/nqt2022-homepage.png";
import "./HomePage.scss";
import Spinner from "../../Components/Spinner/Spinner";
import { useLocation, useNavigate, useParams } from "react-router";
import { isEmpty, isNumber } from "lodash";
import { IEncryptedValue } from "../../shared";
import PasswordResetModal from "../../Components/Modals/PasswordResetModal/PasswordResetModal";
import { TestLaunchResult } from "../../Components/TestCodeButton/models";
import TestModal from "../../Components/Modals/TestModal/TestModal";
import { BookMarkType, ErrorModal, ProcessBookmarkReturn, TEST_CODE_QUERY } from "./models";
import {
  processBookmark,
  isValidBookmarkFormat,
} from "./models/HomePage.utils";
import SgyPanel from "../../Components/SgyPanel/SgyPanel";

const DEEP_LINK_ERROR_QUERY = "deepLinkError";

function HomePage(): JSX.Element {
  const dispatch = useDispatch();

  // routing state
  const navigate = useNavigate();
  const location = useLocation();
  const { bookmarkID } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const controls = useSelector(selectControls);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [invalidIsOpen, setInvalidIsOpen] = useState<boolean>(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [encryption, setEncryption] = useState<string>("");
  const [passwordResetIsOpen, setPasswordResetIsOpen] =
    useState<boolean>(false);
  const [testCodeFromLink, setTestCodeFromLink] = useState<string>("");
  const [resetTestCode, setResetTestCode] = useState<boolean>(false);
  const [errorModal, setErrorModal] = useState<ErrorModal>({} as ErrorModal);
  const [isDeepLinkErrorOpen, setIsDeepLinkErrorOpen] = useState<boolean>(false);
  const [deepLinkErrorMessage, setDeepLinkErrorMessage] = useState<string>("");
  const [quizParams, setQuizParams] = useState<{ subject: string | null; cohort: string | null } | null>(null);
  const onClickQuiz = useCallback(()=> {
    setIsTestModalOpen(true);
  }, []);

  const openTestModalHandler = useCallback(
    (testLaunchResult: TestLaunchResult) => {
      resetTestCode && setResetTestCode(false);
      if (!testLaunchResult.errMsg) {
        setIsTestModalOpen(true);
      }
    },
    [resetTestCode]
  );

  useEffect(() => {
    if (location.pathname === "/popquiz") {
      const searchParams = new URLSearchParams(location.search);
      const subject = searchParams.get('subject');
      const cohort = searchParams.get('cohort');
      let paramSubject : string | null = null;
      let paramCohort : string | null = null;
      if (subject && subject.length === 3 && ['MAT', 'RED', 'SCI', 'GEO', 'HIS', 'CIV'].includes(subject.toUpperCase())){
        paramSubject = subject;
      }
      if (cohort && cohort.length === 1 && isNumber(parseInt(cohort))) {
        paramCohort = cohort;
      }
      setQuizParams({ subject: paramSubject, cohort: paramCohort });
      onClickQuiz();
    }
  }, [location.pathname, location.search, onClickQuiz]);

  // location.state populated when coming from UserAdmin page
  useEffect(() => {
    if (!isEmpty(location.state)) {
      const state = location.state as IEncryptedValue;
      // console.log("TCL: state", state);
      window.history.replaceState({}, document.title);
      if (state.isValid) {
        setEncryption(state.e ? state.e : "");
        setPasswordResetIsOpen(true);
      } else {
        setInvalidIsOpen(true);
        location.search;
      }
    }
  }, [location, location.state]);

  useEffect(() => {
    const deepLinkError = searchParams.get(DEEP_LINK_ERROR_QUERY);

    if (deepLinkError) {
      setDeepLinkErrorMessage(deepLinkError);
      setIsDeepLinkErrorOpen(true);

      const params = new URLSearchParams(searchParams);
      params.delete(DEEP_LINK_ERROR_QUERY);
      setSearchParams(params);
    }
  }, [searchParams, setSearchParams]);

  // searchParams populated when coming from test ticket link
  useEffect(() => {
    if (searchParams.get(TEST_CODE_QUERY)) {
      setTestCodeFromLink(searchParams.get(TEST_CODE_QUERY) as string);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // when a modal is open, prevent background scrolling
  useEffect(() => {
    if(isTestModalOpen || invalidIsOpen || passwordResetIsOpen || isDeepLinkErrorOpen ){
      document.body.classList.add('bg-noscroll');
    } else {
      if(document.body.classList.contains('bg-noscroll')){
        document.body.classList.remove('bg-noscroll');
      }
    }
  }, [isTestModalOpen, invalidIsOpen, passwordResetIsOpen, isDeepLinkErrorOpen]);

  // check :bookmarkID in URL is populated, if valid redirects to searchquestions page
  useEffect(() => {
    // note: searchParam does not populate when params is populated
    if (bookmarkID && location.search) {
      const query = location.search.substring(1);
      // console.log('TCL: query', query);
      if (isValidBookmarkFormat(bookmarkID, query)) {
        processBookmark(
          BookMarkType[bookmarkID as keyof typeof BookMarkType],
          query,
          setErrorModal,
          setIsLoading,
          dispatch
        ).then((response: ProcessBookmarkReturn) => {
          if (response.flag) {
            // populate location.state so we can manually populate QueryPanel component on redirect
            const state = response.defTab? { isBookmark: true, defTab: response.defTab }
              : { isBookmark: true };
            // navigate to searchquestions page with query params of state object
            const queryParams = new URLSearchParams(Object.entries(state).map(([key, value]) => [key, String(value)])).toString();
            navigate(`/searchquestions?${queryParams}`);
          } else {
            navigate("/");
          }
        }).catch((err) => {
          console.error("Caught bookmark error: ", err);
          return navigate("/");
        });
      } else {
        navigate("/");
      }
    } else {
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookmarkID]);

  if (bookmarkID) {
    return <>{isLoading && <Spinner size={130} />}</>;
  }

  return (
    <main className="nqt-homepage">
      <TestModal
        open={isTestModalOpen}
        onClose={() => {
          setResetTestCode(true);
          setIsTestModalOpen(false);
        }}
        subject={quizParams?.subject || null }
        cohort={quizParams?.cohort || null }
      />
      <PasswordResetModal
        e={encryption}
        open={passwordResetIsOpen}
        onClose={() => setPasswordResetIsOpen(false)}
      />
      <Modal
        headerText={errorModal.headerText}
        bodyText={errorModal.bodyText}
        open={!!errorModal.bodyText}
        onClose={() => setErrorModal({} as ErrorModal)}
        windowType="DESC"
      />
      <Modal
        headerText={controls?.ResetHeader?.controlText}
        bodyText={controls?.AccountResetInvalid?.controlText}
        open={invalidIsOpen}
        onClose={() => setInvalidIsOpen(false)}
        windowType="DESC"
      />
      <DeepLinkErrorModal
        open={isDeepLinkErrorOpen}
        message={deepLinkErrorMessage}
        onClose={() => setIsDeepLinkErrorOpen(false)}
      />
      {(isLoading) && <Spinner size={130} />}
      <div className="nqt-homepage--content">
        <div className="nqt-homepage-col-left">
          <section className="nqt-homepage--teacher-sec pt-3">
            <SgyPanel />
          </section>
          <section className="nqt-homepage--student-sec">
            <hgroup className="nqt-homepage--student-hgroup">
              <h3 className="nqt-homepage--student-header">
                {!!controls && controls?.ForStudents?.controlText}
              </h3>
              <hr></hr>
              <label
                htmlFor="nqt-homepage--test-code-input"
                className="nqt-homepage--student-subheader"
              >
                {!!controls && controls?.EnterTestID?.controlText}
              </label>
            </hgroup>
            <TestCodeButton
              onButtonClick={openTestModalHandler}
              reset={resetTestCode}
              testCodeFromLink={testCodeFromLink}
            />
          </section>
        </div>
        <div className="nqt-homepage-col-right">
          <section className="nqt-homepage--enqt-sec">
            <img
              className="nqt-homepage--enqt-img"
              src={HomePageImage}
              alt={controls?.TakeNAEPNowImage?.controlText}
              title={controls?.TakeNAEPNowImage?.controlText}
            />
            <div className="nqt-homepage--enqt-btn-container">
              <button
                className="nqt-homepage--enqt-btn"
                onClick={() => onClickQuiz()}
                type="button"
              >
                <div className="nqt-homepage--enqt-a w-100">
                  <span className="nqt-homepage--enqt-a-text w-100">
                    <div className="nqt-homepage--enqt-a-text-1">
                      <div className="nqt-homepage--try-header-btn-header header-1">
                        {!!controls && controls?.TryHeader?.controlText}
                      </div>
                      <div className="nqt-homepage--try-header-btn-header header-2">
                        {!!controls && controls?.TakeNAEPNow3?.controlText}
                      </div>
                    </div>
                  </span>
                </div>
              </button>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}

export default HomePage;
